import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import isHolidayDate from '../../utils/holidays';
import { filter } from 'lodash';

import socket from '../../utils/socket';

const initialState = {
  clients: [],
  clientId: null,
  isLoading: false,
  error: false,
  holidayInBetween: [],
  holidaysQuote: [],
  startDate: moment().toDate(),
  endDate: moment().toDate(),
  profilStart: moment().toDate(),
  profilEnd: moment().toDate(),
  date: moment().toDate(),
  errorTypes: [],
  activities: [],
  selectedActivity: '',
  prestationTypes: [],
  availableDays: [],
  workedDays: [],
  selectedPrestationType: null,
  siteName: '',
  siteId: null,
  siteSectorId: null,
  address: '',
  addressComplement: '',
  city: '',
  postcode: '',
  text: '',
  startTime: '',
  endTime: '',
  canValidateStepPrestation: true
};

const slice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    // LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.error(state.error);
    },

    getClients(state, action) {
      state.clients = action.payload;
      // CW-22029 : guard sur payload vide (user client sans client associe)
      state.clientId = action.payload?.[0]?.id ?? null;
    },
    setClientId(state, action) {
      state.clientId = action.payload;
    },
    resetStateForm(state, action) {
      const { sites } = action.payload;
      state.startDate = moment().toDate();
      state.endDate = moment().toDate();
      state.profilStart = moment().toDate();
      state.profilEnd = moment().toDate();
      state.date = moment().toDate();
      state.errorTypes = [];
      // CW-22029 : guard sur sites vide (user client sans site associe).
      // Sans guard, sites[0].id crashait au reset du form (declenche par
      // NewQuoteModal a l'annulation ou apres envoi de la demande de devis).
      const firstSite = sites?.[0];
      if (firstSite) {
        state.siteId = firstSite.id;
        state.clientId = firstSite.client_id;
        state.siteName = firstSite.name;
        state.address = firstSite.address;
        state.addressComplement = firstSite.address_complement;
        state.city = firstSite.city;
        state.postcode = firstSite.postcode;
      } else {
        state.siteId = 'new';
        state.siteName = '';
        state.address = '';
        state.addressComplement = '';
        state.city = '';
        state.postcode = '';
      }
      state.text = '';
    },
    setCanValidateStepPrestation(state, action) {
      state.canValidateStepPrestation = action.payload;
    },
    setWorkedDays(state, action) {
      state.workedDays = action.payload;
    },
    setActivities(state, action) {
      state.activities = action.payload;
    },
    setSelectedActivity(state, action) {
      state.selectedActivity = action.payload;
    },
    setPrestationTypes(state, action) {
      state.prestationTypes = action.payload;
    },
    setSelectedPrestationType(state, action) {
      state.selectedPrestationType = action.payload;
    },
    setDates(state, action) {
      const { start = null, end = null, date = null } = action.payload;
      if (start) {
        if (start > state.endDate) {
          state.endDate = start;
        }
        if (start > state.profilEnd) {
          state.profilEnd = start;
          state.holidayInBetween = [];
        }
        if (start > state.date) {
          state.date = start;
        }
        state.startDate = start;
        state.profilStart = start;
      }
      if (end) {
        state.endDate = end;
        state.profilEnd = end;
      }
      if (end < start) {
        state.endDate = start;
      }
      if (date) {
        state.date = date;
      }
    },
    setProfileDates(state, action) {
      const { start, end, holidays } = action.payload;

      if (start > end) {
        state.profilEnd = start;
        state.profilStart = start;
      } else {
        state.profilStart = start;
        state.profilEnd = end;
      }

      const filteredHolidays = filter(
        holidays,
        (holiday) =>
          moment(holiday.month_day, 'MM-DD').isBetween(moment(start), moment(end), null, '()') &&
          (!holiday.sectors_geo_chain?.length || holiday.sectors_geo_chain.includes(`/${state.siteSectorId}/`))
      );

      const arrayOfHolidays = [];
      holidays.forEach((item) => {
        const interval = moment(end).format('YYYY') - moment(start).format('YYYY');
        for (let i = 0; i <= interval; i += 1) {
          const year = moment(start).add(i, 'year').format('YYYY');
          const dateToCheck = `${item.month_day}-${year}`;
          const dateToCheckHoliday = moment(dateToCheck, 'MM-DD-YYYY').format('YYYY-MM-DD');

          const isHolidayForForm = isHolidayDate({
            holidays: filteredHolidays,
            date: dateToCheckHoliday,
            sectorGeoId: state.siteSectorId
          });

          if (isHolidayForForm) {
            arrayOfHolidays.push(moment(dateToCheck, 'MM-DD-YYYY').isoWeekday());
          }
        }
      });
      state.holidayInBetween = arrayOfHolidays;
      state.holidaysQuote = filteredHolidays;
    },

    setAvailableDays(state) {
      const start = moment(state.profilStart).startOf('day');
      const interval = moment(state.profilEnd).startOf('day').diff(start, 'days');
      if (interval === 0) {
        const day = start.day();
        state.availableDays = [day];
        state.workedDays = [day];
      } else if (interval >= 7) {
        state.availableDays = [1, 2, 3, 4, 5, 6, 7];
        state.workedDays = [1, 2, 3, 4, 5, 6, 7];
      } else {
        const arrayOfDayAvailable = [];
        for (let idx = 0; idx <= interval; idx += 1) {
          arrayOfDayAvailable.push(moment(state.profilStart).startOf('day').add(idx, 'days').isoWeekday());
        }
        state.availableDays = arrayOfDayAvailable;
        state.workedDays = arrayOfDayAvailable;
      }
    },
    setText(state, action) {
      state.text = action.payload;
    },
    setTimes(state, action) {
      const { start = null, end = null } = action.payload;
      if (start) {
        state.startTime = start;
      }
      if (end) {
        state.endTime = end;
      }
    },
    setErrors(state, action) {
      state.errorTypes = action.payload;
    },
    setSiteId(state, action) {
      const { siteId, sites } = action.payload;

      // CW-22029 : si siteId est inconnu dans sites (race condition ou user
      // client sans site qui aurait recu un siteId obsolete), fallback sur
      // 'new' au lieu de crasher sur `site.name` (find = undefined).
      const site = siteId !== 'new' ? sites?.find((elem) => elem.id === siteId) : null;

      if (site) {
        state.siteId = siteId;
        state.siteName = site.name;
        state.postcode = site.postcode;
        state.city = site.city;
        state.address = site.address;
        state.addressComplement = site.addressComplement;
        state.clientId = site.client_id;
        state.siteSectorId = site.sector_geo_id;
      } else {
        state.siteId = 'new';
        state.siteName = '';
        state.postcode = '';
        state.city = '';
        state.address = '';
        state.addressComplement = '';
        state.siteSectorId = null;
        if (state.clients.length) {
          state.clientId = state.clients[0].id;
        }
      }
    },
    updateFormSite(state, action) {
      const { dataType, value, sites, siteId } = action.payload;
      if (siteId === 'new') {
        switch (dataType) {
          case 'siteName':
            state.siteName = value;
            break;
          case 'city':
            state.city = value;
            break;
          case 'postcode':
            state.postcode = value;
            break;
          case 'address':
            state.address = value;
            break;
          case 'complement':
            state.addressComplement = value;
            break;
          default:
            break;
        }
      }

      if (dataType === 'all') {
        const site = sites.filter((el) => el.id === siteId)[0];
        state.siteName = site ? site.name : '';
        state.postcode = site ? site.postcode : '';
        state.city = site ? site.city : '';
        state.address = site ? site.address : '';
        state.addressComplement = site ? site.addressComplement : '';
      }
    }
  }
});

// Reducer
export default slice.reducer;
// actions
export const {
  resetStateForm,
  setAvailableDays,
  setSelectedPrestationType,
  setSiteId,
  setClientId,
  setCanValidateStepPrestation,
  setDates,
  setProfileDates,
  setErrors,
  setText,
  setTimes,
  setSelectedActivity,
  setWorkedDays,
  setSelectedQuote,
  updateFormSite
} = slice.actions;

export function getActivities() {
  return async (dispatch) => {
    try {
      const resultActivities = await socket.get('/activities');
      dispatch(slice.actions.setActivities(resultActivities.activities));
      dispatch(
        slice.actions.setSelectedActivity(resultActivities.activities[0] ? resultActivities.activities[0].id : null)
      );
      const resultPresta = await socket.get('/prestations/types');

      dispatch(slice.actions.setPrestationTypes(resultPresta.prestationTypes));
      if (resultPresta.prestationTypes[0]) {
        dispatch(slice.actions.setSelectedPrestationType(resultPresta.prestationTypes[0].id));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getClients() {
  return async (dispatch) => {
    try {
      const result = await socket.get('/clients');

      dispatch(slice.actions.getClients(result.clients));
    } catch (err) {
      dispatch(slice.actions.hasError(err));
    }
  };
}
